/**
 * `<ChatSurface>` — the in-app chat surface root: stateful session view, SSE consumer,
 * telemetry display, save-as-note (editor-chat.md §1.3, §2.3, §2.4; ADR 007).
 *
 * The route element mounted at `/chat` (the frontend owns the route *slot* via the
 * `ShellMount`; this workstream owns the *element* — ADR 007 / routes.ts). It composes:
 *  - `<ChatSessionList>` — the session-history sidebar (list / select / rename / new);
 *  - `<ChatMessageList>` — the open session's persisted messages + the in-flight streaming
 *    turn from `useChatStream` (the SSE consumer — ADR 007);
 *  - `<ChatComposer>` — the message input (disabled while a turn streams);
 *    (the per-turn/session cost-telemetry strip was removed 2026-05-21);
 *  - the **save-as-note** action on the active-session header — promotes the session to a
 *    `source='chat'` note (editor-chat.md §2.4).
 *
 * editor-chat.md §1.1 / §1.3 — graceful degradation is load-bearing: when the chat model is
 * unavailable, the *non-model affordances* (session list, history, rename, save-as-note,
 * telemetry view) keep working; only the *turn* fails, and it fails into a designed inline
 * state (the `error` event the `useChatStream` reducer surfaces). The surface never blanks.
 *
 * State ownership: `useChatStream` owns the *in-flight* turn; this surface owns the *session*
 * (the open session id, its fetched history) and refetches the session after a turn completes
 * so the just-streamed turn becomes persisted history.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon, useConfirmModal } from '@/components';
import { useChatViewStore } from '@/stores';
// v2 refit (2026-05-17): the page-head now uses the global .page-head__eyebrow
// CSS pattern; <Eyebrow> + <HeroBackdrop> are no longer rendered here. The
// imports stay shaped so other surfaces that still want them can import from
// the same barrel.
import { getLogger } from '@/api';
import {
  clearChatHistory,
  getChatSession,
  listChatSessions,
  renameChatSession,
  saveChatSessionAsNote,
} from './chatApi';
import { useChatStream } from './useChatStream';
import { NewChatModal } from './NewChatModal';
import { ChatSessionList } from './ChatSessionList';
import { ChatMessageList } from './ChatMessageList';
import { ChatComposer } from './ChatComposer';
import { pickSampleQueries } from './terminalSampleQueries';
import { formatRelativeTime } from './sessionTime';
import { isAdoptableSession } from './chatTypes';
import type { ChatSessionSummary, ChatTokenUsage } from './chatTypes';
// U7 remaster (2026-07-05): chat-local styles — bottom-docked prompt, idle state,
// destructive Clear-history treatment. Loads before app.css (see file header).
import './chat-surface.css';

const log = getLogger('chat.surface');

/** An all-zero token usage — the session-aggregate before any turn has run. */
const ZERO_USAGE: ChatTokenUsage = {
  inputTokens: 0,
  outputTokens: 0,
  cacheCreationTokens: 0,
  cacheReadTokens: 0,
};

/** `@tanstack/query` keys for the chat surface's data — scoped under a `chat` namespace. */
const chatKeys = {
  sessions: ['chat', 'sessions'] as const,
  session: (id: string) => ['chat', 'session', id] as const,
};

/** How many recent sessions the idle state lists above the prompt. */
const IDLE_RECENT_LIMIT = 5;

/**
 * `<ChatIdleState>` — what the space above the prompt shows when no conversation is open
 * (U7, design-review P3 item 9): the most recent sessions as quiet `›` lines (click to
 * reopen — the same data the rail's `<ChatSessionList>` renders, reused compactly) and
 * three sample prompts as dashed chips (click to prefill the prompt — the same
 * `terminalSampleQueries` pool the composer's idle ticker cycles). Mono, quiet, no cards —
 * it reads as the terminal's MOTD, not a dashboard.
 */
function ChatIdleState({
  sessions,
  onSelectSession,
  onPickPrompt,
}: {
  sessions: ChatSessionSummary[];
  onSelectSession: (sessionId: string) => void;
  onPickPrompt: (prompt: string) => void;
}): JSX.Element {
  // Three sample prompts, picked once per mount so they hold still while on screen.
  const [prompts] = useState<readonly string[]>(() => pickSampleQueries(3));
  const recent = sessions.slice(0, IDLE_RECENT_LIMIT);

  return (
    <div className="chat-idle" data-testid="chat-idle">
      {recent.length > 0 && (
        <section>
          <div className="chat-idle__head label-mono">recent sessions</div>
          <ul className="chat-idle__sessions">
            {recent.map((session) => (
              <li key={session.id}>
                <button
                  type="button"
                  className="chat-idle__session"
                  onClick={() => onSelectSession(session.id)}
                  title="Reopen this session"
                >
                  <span className="chat-idle__session-mark" aria-hidden="true">
                    ›
                  </span>
                  <span className="chat-idle__session-title">{session.title}</span>
                  <span className="chat-idle__session-time">{formatRelativeTime(session)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section>
        <div className="chat-idle__head label-mono">try asking</div>
        <div className="chat-idle__chips">
          {prompts.map((prompt) => (
            <button
              type="button"
              key={prompt}
              className="chat-idle__chip"
              onClick={() => onPickPrompt(prompt)}
              title="Put this question in the prompt — edit or press Enter to send"
            >
              {prompt}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

/**
 * The in-app chat surface. Owns the open-session state; composes the session list, the
 * message list (persisted + streaming), the composer, the telemetry display, and the
 * save-as-note action.
 */
export function ChatSurface(): JSX.Element {
  const queryClient = useQueryClient();
  // Branded confirm dialog for the destructive "clear history" action.
  const { modal: confirmModal, confirm } = useConfirmModal();
  // The open session — `null` means "a fresh, not-yet-created session": the composer's first
  // turn omits `sessionId`, the backend creates one, and the surface adopts it from the
  // session list refetch. Held in `useChatViewStore` (sessionStorage-backed) so navigating
  // away from /chat and back — or a reload within the session — reopens the same conversation.
  const activeSessionId = useChatViewStore((s) => s.activeSessionId);
  const setActiveSessionId = useChatViewStore((s) => s.setActiveSessionId);
  // The save-as-note action's last outcome — surfaced on the header.
  const [saveOutcome, setSaveOutcome] = useState<string | null>(null);
  // facelift-v3 — the guided "New chat" modal (first prompt + workflow + scope).
  const [newChatOpen, setNewChatOpen] = useState(false);
  // The recent-sessions rail is collapsible and starts collapsed — the console gets the full
  // width by default; the header toggle reveals the history list on demand. Persisted so the
  // rail state survives navigation / reload within the session.
  const railCollapsed = useChatViewStore((s) => s.railCollapsed);
  const setRailCollapsed = useChatViewStore((s) => s.setRailCollapsed);
  // The composer draft's setter — the idle state's sample-prompt chips prefill through it
  // (the draft itself lives in the store; <ChatComposer> renders it).
  const setDraft = useChatViewStore((s) => s.setDraft);
  // The query of the in-flight turn — fed to the "thinking" indicator so it can run a real
  // hybrid search on it and stream the results out as latency-masking filler.
  const [pendingQuery, setPendingQuery] = useState('');

  // The SSE consumer — owns the in-flight turn (ADR 007 — the frontend is the stream consumer).
  // `cancelTurn` (perf D5) aborts the in-flight stream — the surface calls it on session
  // switch / new-chat / clear-history so a still-streaming old turn can't write into the new
  // view (the hook also aborts on unmount).
  const { turn, isStreaming, sendTurn, cancelTurn, reset } = useChatStream();

  // The terminal scroll viewport — kept pinned to the bottom so the inline prompt (the last
  // line of the console) stays in view as output streams in (terminal-style auto-scroll).
  const scrollRef = useRef<HTMLDivElement>(null);

  // --- data fetching --------------------------------------------------------
  // The session list — the history sidebar.
  const sessionsQuery = useQuery({
    queryKey: chatKeys.sessions,
    queryFn: async () => (await listChatSessions()).items,
  });

  // The open session's full history — only fetched when a session is open.
  const sessionQuery = useQuery({
    queryKey: chatKeys.session(activeSessionId ?? ''),
    enabled: activeSessionId !== null,
    queryFn: () => getChatSession(activeSessionId as string),
  });

  // --- turn lifecycle: refetch the session + the list when a turn finishes --
  useEffect(() => {
    if (turn === null) return;
    if (turn.phase === 'done' || turn.phase === 'error') {
      // The turn completed (or ended in a kept-partial error) — the agent loop persisted it.
      // Refetch the open session so the streamed turn becomes persisted history, and the
      // list so a freshly-created session appears + ordering updates.
      void queryClient.invalidateQueries({ queryKey: chatKeys.sessions });
      if (activeSessionId !== null) {
        void queryClient.invalidateQueries({ queryKey: chatKeys.session(activeSessionId) });
      }
    }
    // Only react to a phase transition — `turn` identity changes on every event, but the
    // refetch should fire once, on reaching a terminal phase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turn?.phase]);

  // --- dedupe: drop the streaming view once the persisted refetch has it ----
  // After the post-turn refetch lands, the session's persisted `messages` include the
  // assistant turn we just streamed. Without this cleanup, both <ChatMessageList>'s
  // persisted-render loop AND its in-flight `streamingTurn` block render the same bubble
  // — the visible duplicate. The signal that the persisted view is now authoritative:
  // the just-fetched messages contain a message whose id === turn.messageId (the backend
  // echoes it in the `done` / `error` event). Once that's true, drop the streaming view.
  useEffect(() => {
    if (turn === null) return;
    if (turn.phase === 'streaming') return; // still streaming — keep the bubble live
    if (turn.messageId === null) return; // missing id (shouldn't happen) — leave the view
    const messages = sessionQuery.data?.messages ?? [];
    if (messages.some((m) => m.id === turn.messageId)) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionQuery.data, turn?.phase, turn?.messageId]);

  // When the session list refetches after a turn that *created* a session, adopt the newest
  // session as active (the first turn of a fresh chat had no `sessionId`). Ghost-session
  // guard (perf D5): only adopt a session that actually has content — a failed turn can
  // leave an empty shell at the top of the list, and auto-opening it strands the user in a
  // blank conversation (this client's own just-created session always has content, because
  // the backend persists the user message at turn start).
  useEffect(() => {
    if (activeSessionId === null && sessionsQuery.data && sessionsQuery.data.length > 0) {
      // The most-recently-updated session is first — if a turn just created one, it is this.
      const newest = sessionsQuery.data[0];
      if (
        turn !== null &&
        (turn.phase === 'done' || turn.phase === 'error') &&
        isAdoptableSession(newest)
      ) {
        setActiveSessionId(newest.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionsQuery.data, turn?.phase]);

  // --- callbacks ------------------------------------------------------------
  const handleSend = useCallback(
    (message: string, opts?: { workflow?: string | null; mode?: 'fast' | 'deep' }) => {
      // Stash the query so the thinking indicator can preview a real hybrid search on it.
      setPendingQuery(message);
      // Send the turn — omit `sessionId` for a fresh chat; the backend creates the session and
      // the post-turn refetch adopts it. `opts.workflow` carries the composer's routing select;
      // `opts.mode` carries the Fast/Deep tool-surface tier (default 'fast').
      void sendTurn({
        ...(activeSessionId !== null ? { sessionId: activeSessionId } : {}),
        userMessage: message,
        workflow: opts?.workflow ?? null,
        mode: opts?.mode ?? 'fast',
      });
    },
    [activeSessionId, sendTurn],
  );

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      // Re-selecting the already-open session is a no-op — do NOT abort its in-flight turn.
      if (sessionId === activeSessionId) return;
      // D5: abort the previous session's in-flight stream FIRST — without this, its late
      // events resurrect the old turn's bubble inside the newly-opened session's view,
      // `isStreaming` stays true (locking the composer), and the stale `pendingQuery` echo
      // renders above it. `cancelTurn` also unlocks the composer synchronously.
      cancelTurn();
      setActiveSessionId(sessionId);
      setSaveOutcome(null);
      setPendingQuery(''); // the old turn's echo must not follow us into the new session
      reset(); // clear any in-flight turn view-model from the previously-open session
    },
    [activeSessionId, cancelTurn, reset],
  );

  const handleNewSession = useCallback(() => {
    cancelTurn(); // D5: a fresh chat must not inherit the old session's in-flight stream
    setActiveSessionId(null);
    setSaveOutcome(null);
    setPendingQuery('');
    reset();
  }, [cancelTurn, reset]);

  // An idle-state sample chip prefills the prompt (it does NOT send — the user can edit
  // first), then focuses the entry so Enter fires immediately. The entry is looked up by
  // its class: <ChatComposer> owns the textarea ref and the two components are siblings.
  const handlePickPrompt = useCallback(
    (prompt: string) => {
      setDraft(prompt);
      requestAnimationFrame(() => {
        document
          .querySelector<HTMLTextAreaElement>('.chat-term-entry')
          ?.focus({ preventScroll: true });
      });
    },
    [setDraft],
  );

  // Clear ALL chat history — confirm first (destructive), then wipe server-side and reset to a
  // fresh session. Sessions already saved as notes keep their note; only chat state is cleared.
  const handleClearHistory = useCallback(async () => {
    const ok = await confirm({
      eyebrow: 'chat',
      title: 'Clear chat history?',
      message:
        'This permanently deletes every chat session and its messages. Sessions you saved as ' +
        'notes keep their note. This cannot be undone.',
      confirmLabel: 'Clear history',
      destructive: true,
    });
    if (!ok) return;
    try {
      cancelTurn(); // D5: the sessions are going away — tear down any in-flight stream too
      await clearChatHistory();
      setActiveSessionId(null);
      setSaveOutcome(null);
      setPendingQuery('');
      reset();
      await queryClient.invalidateQueries({ queryKey: chatKeys.sessions });
    } catch (err) {
      log.error('chat_clear_history_failed', { error: String(err) });
      setSaveOutcome('Could not clear chat history.');
    }
  }, [cancelTurn, confirm, queryClient, reset]);

  // facelift-v3 — start a fresh session AND send the first turn with the chosen
  // workflow/scope hints (NewChat modal). Sends with no sessionId so the backend
  // mints the session; the post-turn refetch adopts it.
  const handleStartNewChat = useCallback(
    (message: string, opts: { workflow: string | null; scope: string | null }) => {
      // D5: abort any in-flight turn first — it frees the hook's send slot synchronously,
      // so the fresh chat's first turn is not swallowed by the double-send guard.
      cancelTurn();
      setActiveSessionId(null);
      setSaveOutcome(null);
      reset();
      setPendingQuery(message);
      void sendTurn({ userMessage: message, workflow: opts.workflow, scope: opts.scope });
      setNewChatOpen(false);
    },
    [cancelTurn, reset, sendTurn],
  );

  const handleRename = useCallback(
    (sessionId: string, title: string) => {
      void renameChatSession(sessionId, title)
        .then(() => queryClient.invalidateQueries({ queryKey: chatKeys.sessions }))
        .catch((err) => log.error('chat_rename_failed', { sessionId, error: String(err) }));
    },
    [queryClient],
  );

  const handleSaveAsNote = useCallback(() => {
    if (activeSessionId === null) return;
    setSaveOutcome('Saving…');
    void saveChatSessionAsNote(activeSessionId)
      .then((result) => {
        // The action's outcome is *data*, not an exception (the backend surfaces failures in
        // the result shape) — render it on the header.
        setSaveOutcome(result.message);
        if (result.ok) {
          // The session now carries `savedAsNoteId` — refetch so the list badge + the header
          // reflect it.
          void queryClient.invalidateQueries({ queryKey: chatKeys.sessions });
          void queryClient.invalidateQueries({
            queryKey: chatKeys.session(activeSessionId),
          });
        }
      })
      .catch((err) => {
        log.error('chat_save_as_note_failed', { activeSessionId, error: String(err) });
        setSaveOutcome('Could not save this session as a note.');
      });
  }, [activeSessionId, queryClient]);

  // --- render ---------------------------------------------------------------
  const openSession = sessionQuery.data ?? null;
  const persistedMessages = openSession?.messages ?? [];
  const sessionUsage = openSession?.usage ?? ZERO_USAGE;

  // Idle = no session open AND no turn in flight (a fresh chat's first turn runs with
  // activeSessionId still null, so `turn === null` is load-bearing — `sendTurn` sets the
  // view-model synchronously, so the idle block never flashes over a streaming turn).
  const isIdle = activeSessionId === null && turn === null;

  // Keep the console pinned to the bottom as content grows — the inline prompt is the last
  // line, so new output (a fresh turn, streamed text, tool rows) should scroll it into view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [persistedMessages.length, turn?.phase, turn?.text, turn?.toolCalls.length]);

  // UX-reconciliation rip-and-replace — render the standalone's Chat chrome verbatim
  // (`docs/refactor/_extracted-standalone-source/10_5049f3c3.jsx` ChatScreen).
  //   - <PageHead> with title, sub-description, and a row of action chips: session
  //     telemetry, "Save as note", "New chat".
  //   - <div.page-body.flush> as a 240px / 1fr grid (height: 100%, no padding) — the
  //     standalone's "flush" modifier zeroes the surface padding so the chat list +
  //     conversation column own their own edges.
  //   - Conversation column centers a max-width 760 message stream; the composer pins
  //     to the bottom inside a `border-top: 1px solid var(--line)` footer.

  // The token-usage chip renders only once the session has usage. Its old zero-state label
  // ("New session") sat beside the "+ New chat" button and read as a duplicate control
  // (design review 2026-07-05) — a fresh session simply shows no chip.
  const hasSessionUsage = sessionUsage.inputTokens + sessionUsage.outputTokens > 0;
  const sessionTokensLabel = `Session · ${sessionUsage.inputTokens.toLocaleString()} in · ${sessionUsage.outputTokens.toLocaleString()} out`;

  const saveAsNoteLabel =
    openSession?.savedAsNoteId !== null && openSession !== null && openSession?.savedAsNoteId !== undefined
      ? 'Saved as note'
      : 'Save as note';

  return (
    <div className="surface surface--chat">
      {newChatOpen && (
        <NewChatModal onClose={() => setNewChatOpen(false)} onStart={handleStartNewChat} />
      )}
      {confirmModal}
      {/* v2 refit (2026-05-17): the hero-serif variant was dropped (no serif
          in the v2 design language). page-head uses the standard global
          chrome with the eyebrow + lowercase Major Mono title pattern.
          HeroBackdrop is gone — the marketing v2 sections don't use radial
          gradients; sharp editorial dividers do the heavy lifting. */}
      <div className="page-head">
        <div className="page-head__main">
          {/* A header toggle reveals/hides the collapsible recent-sessions rail
              (collapsed by default so the console owns the full width). */}
          <button
            type="button"
            className="btn btn--sm chat-rail-toggle"
            onClick={() => setRailCollapsed(!railCollapsed)}
            aria-expanded={!railCollapsed}
            title={railCollapsed ? 'Show recent sessions' : 'Hide recent sessions'}
          >
            {railCollapsed ? '» recent' : '« recent'}
          </button>
          <div className="page-head__eyebrow">chat</div>
        </div>
        <div className="page-head__actions">
          {hasSessionUsage && (
            <span className="chip" title="Session token usage">
              {sessionTokensLabel}
            </span>
          )}
          {activeSessionId !== null && (
            <button
              type="button"
              className="btn btn--sm chat-surface__save-as-note"
              onClick={handleSaveAsNote}
              disabled={openSession === null}
              title="Save the current session as a source='chat' note"
            >
              <Icon name="archive" size={12} />
              {saveAsNoteLabel}
            </button>
          )}
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setNewChatOpen(true)}
            title="Start a fresh chat — pick a workflow + scope"
          >
            <Icon name="plus" size={12} />
            New chat
          </button>
          {/* Destructive — .btn--danger + the useConfirmModal gate in handleClearHistory. */}
          <button
            type="button"
            className="btn btn--sm btn--danger"
            onClick={() => void handleClearHistory()}
            disabled={(sessionsQuery.data?.length ?? 0) === 0}
            title="Delete every chat session and its messages"
          >
            <Icon name="trash" size={12} />
            Clear history
          </button>
          {saveOutcome !== null && (
            <span className="chat-surface__save-outcome" role="status">
              {saveOutcome}
            </span>
          )}
        </div>
      </div>

      <div
        className={`page-body flush chat-surface${
          railCollapsed ? ' chat-surface--rail-collapsed' : ''
        }`}
        data-testid="chat-surface"
      >
        {/* The session-history sidebar — works without the chat model (editor-chat.md §1.3). */}
        <ChatSessionList
          sessions={sessionsQuery.data ?? []}
          activeSessionId={activeSessionId}
          isLoading={sessionsQuery.isLoading}
          onSelect={handleSelectSession}
          onNew={handleNewSession}
          onRename={handleRename}
        />

        <div className="chat-surface__main chat-surface__main--term">
          {/* The terminal console. U7 (2026-07-05): the prompt is DOCKED at the bottom of the
              column (classic terminal/chat position) — the scroll surface above it holds the
              scrollback, bottom-anchored so short output hugs the prompt (chat-surface.css).
              The surface's scroll-pinning effect still targets .chat-conv-scroll. */}
          <div className="chat-conv-scroll" ref={scrollRef}>
            <div className="chat-conv-inner">
              {/* Idle state — no session open, no turn in flight: recent sessions + sample
                  prompts above the prompt line (design-review P3 item 9). */}
              {isIdle && (
                <ChatIdleState
                  sessions={sessionsQuery.data ?? []}
                  onSelectSession={handleSelectSession}
                  onPickPrompt={handlePickPrompt}
                />
              )}
              <ChatMessageList
                messages={persistedMessages}
                streamingTurn={turn}
                isStreaming={isStreaming}
                pendingUserQuery={pendingQuery}
              />
            </div>
          </div>
          {/* The prompt (boot line + flag controls + |< entry), anchored below the scrollback. */}
          <div className="chat-term-dock">
            <ChatComposer onSend={handleSend} isStreaming={isStreaming} />
          </div>
        </div>
      </div>
    </div>
  );
}
